import type { ConsentDecision } from './consentTypes.js';
import { resolveConsentStorageTenantCode } from './consentStorageTenant.js';

const CURRENT_KEY_PREFIX = 'customer:consent:current:';
const TENANT_REJECT_UNTIL_PREFIX = 'customer:consent:reject-until:';
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;
export const GLOBAL_ACCEPT_ALL_KEY = 'customer:consent:global:accept-all';
const REJECT_SUPPRESSION_MS = 183 * 24 * 60 * 60 * 1000;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface RejectUntilRecord {
  version: string;
  rejectUntilMs: number;
}

interface ReadEffectiveConsentInput {
  tenantCode: string;
  consentVersion: string;
  loyaltyProfilingAvailable: boolean;
  storage: StorageLike | null;
  clockMs?: () => number;
}

interface WriteConsentDecisionInput {
  tenantCode: string;
  decision: ConsentDecision;
  loyaltyProfilingAvailable: boolean;
  storage: StorageLike | null;
  clockMs?: () => number;
}

function currentKey(tenantCode: string): string {
  return `${CURRENT_KEY_PREFIX}${resolveConsentStorageTenantCode(tenantCode)}`;
}

function rejectUntilKey(tenantCode: string): string {
  return `${TENANT_REJECT_UNTIL_PREFIX}${resolveConsentStorageTenantCode(tenantCode)}`;
}

function parseConsentDecision(raw: string | null): ConsentDecision | null {
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as ConsentDecision;
    if (
      parsed.necessary === true &&
      typeof parsed.functional === 'boolean' &&
      typeof parsed.analytics === 'boolean' &&
      typeof parsed.marketing === 'boolean' &&
      typeof parsed.profiling === 'boolean' &&
      typeof parsed.version === 'string' &&
      typeof parsed.decidedAtMs === 'number'
    ) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function parseRejectUntil(raw: string | null): RejectUntilRecord | null {
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as RejectUntilRecord;
    if (
      typeof parsed.version === 'string' &&
      typeof parsed.rejectUntilMs === 'number'
    ) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function isFresh(decidedAtMs: number, nowMs: number): boolean {
  return nowMs - decidedAtMs <= CONSENT_TTL_MS;
}

export function isAcceptAllDecision(
  decision: ConsentDecision,
  opts: { loyaltyProfilingAvailable: boolean }
): boolean {
  const baseAccepted =
    decision.necessary &&
    decision.functional &&
    decision.analytics &&
    decision.marketing;
  if (!baseAccepted) {
    return false;
  }
  if (opts.loyaltyProfilingAvailable) {
    return decision.profiling === true;
  }
  return true;
}

export function readEffectiveConsent(
  input: ReadEffectiveConsentInput
): ConsentDecision | null {
  const {
    tenantCode,
    consentVersion,
    loyaltyProfilingAvailable,
    storage,
    clockMs = Date.now,
  } = input;
  if (storage === null) {
    return null;
  }
  const nowMs = clockMs();
  const tenantRaw = parseConsentDecision(storage.getItem(currentKey(tenantCode)));
  if (tenantRaw !== null) {
    if (tenantRaw.version !== consentVersion || !isFresh(tenantRaw.decidedAtMs, nowMs)) {
      storage.removeItem(currentKey(tenantCode));
    } else {
      const needsProfilingDecision =
        loyaltyProfilingAvailable &&
        !isAcceptAllDecision(tenantRaw, { loyaltyProfilingAvailable }) &&
        tenantRaw.functional &&
        tenantRaw.analytics &&
        tenantRaw.marketing;
      if (needsProfilingDecision) {
        return null;
      }
      return tenantRaw;
    }
  }

  const globalRaw = parseConsentDecision(storage.getItem(GLOBAL_ACCEPT_ALL_KEY));
  if (globalRaw !== null) {
    if (globalRaw.version !== consentVersion || !isFresh(globalRaw.decidedAtMs, nowMs)) {
      storage.removeItem(GLOBAL_ACCEPT_ALL_KEY);
    } else if (
      isAcceptAllDecision(globalRaw, {
        loyaltyProfilingAvailable,
      })
    ) {
      const hydrated: ConsentDecision = {
        ...globalRaw,
      };
      storage.setItem(currentKey(tenantCode), JSON.stringify(hydrated));
      return hydrated;
    }
  }

  const rejectUntil = parseRejectUntil(storage.getItem(rejectUntilKey(tenantCode)));
  if (
    rejectUntil !== null &&
    rejectUntil.version === consentVersion &&
    rejectUntil.rejectUntilMs > nowMs
  ) {
    return {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      profiling: false,
      version: consentVersion,
      decidedAtMs: nowMs,
    };
  }
  if (rejectUntil !== null && rejectUntil.rejectUntilMs <= nowMs) {
    storage.removeItem(rejectUntilKey(tenantCode));
  }

  return null;
}

export function writeConsentDecision(input: WriteConsentDecisionInput): void {
  const {
    tenantCode,
    decision,
    loyaltyProfilingAvailable,
    storage,
    clockMs = Date.now,
  } = input;
  if (storage === null) {
    return;
  }
  const nowMs = clockMs();
  storage.setItem(currentKey(tenantCode), JSON.stringify(decision));

  if (isAcceptAllDecision(decision, { loyaltyProfilingAvailable })) {
    storage.setItem(GLOBAL_ACCEPT_ALL_KEY, JSON.stringify(decision));
    storage.removeItem(rejectUntilKey(tenantCode));
    return;
  }

  storage.removeItem(GLOBAL_ACCEPT_ALL_KEY);
  if (
    decision.functional === false &&
    decision.analytics === false &&
    decision.marketing === false &&
    decision.profiling === false
  ) {
    const suppressUntil = {
      version: decision.version,
      rejectUntilMs: nowMs + REJECT_SUPPRESSION_MS,
    };
    storage.setItem(rejectUntilKey(tenantCode), JSON.stringify(suppressUntil));
  } else {
    storage.removeItem(rejectUntilKey(tenantCode));
  }
}

export function clearTenantConsent(
  tenantCode: string,
  storage: StorageLike | null
): void {
  if (storage === null) {
    return;
  }
  storage.removeItem(currentKey(tenantCode));
}
